import {Dialog, Loader} from '@jobseeker/lotus/components';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import {Box, FormControl, useTheme} from '@mui/material';
import {useFormik} from 'formik';
import {useEffect, useState} from 'react';
import {profileService} from '@jobseeker/common-core/services';
import ButtonV2 from '@jobseeker/lotus/elements/ButtonV2';
import {showSnackbar} from '@jobseeker/lotus/utils';
import CurrentLocation from './CurrentLocation';
import ExperienceYear from './ExperienceYear';
import Phone from './Phone';
import validationSchemaPersonalDetails from './validations';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {
  LEARN_GROW_COURSE_ENQUIRY_PROFILE_COMPLETION,
  HIRISTORGANISATION,
  ORGANISATION,
} from '../../constants';
import Name from './Name';
import CurrentOrganization from './CurrentOrganization';
import {useDispatch, useSelector} from 'react-redux';
import {AppState} from '@jobseeker/common-core/types';

const isAppHirist = process.env.NEXT_PUBLIC_APP_NAME === 'hirist';

type ProfileSchema = {[key: string]: string} | undefined;

const getInfoString = (profileSchema: ProfileSchema) => {
  if (!profileSchema) return '';

  const keys = [
    'pd.phone',
    'pd.expYear',
    'pd.currentLocation',
    'pd.countryCode',
    'pd.name',
    'pd.currentOrganization',
  ];

  return keys
    .map(key => profileSchema[key])
    .filter(Boolean)
    .join('');
};

const getRequiredString = ({
  infoString,
  makeCurrentOrganizationMandatory,
  profileSchema,
}: {
  infoString: string;
  makeCurrentOrganizationMandatory: boolean;
  profileSchema: ProfileSchema;
}) => {
  if (!infoString || !profileSchema) return '';

  if (makeCurrentOrganizationMandatory) {
    return infoString;
  }

  return infoString.replace(profileSchema['pd.currentOrganization'], '');
};

const ProfileInfoPopup = ({
  userId = 0,
  closeEnquiryPopup,
  handleProfileUpdate,
  countriesJson,
}: {
  userId: number;
  countriesJson: {
    countryCode: string;
    countryName: string;
    countryCodeAlpha: string;
  }[];
  closeEnquiryPopup: () => void;
  handleProfileUpdate: () => void;
}) => {
  const theme = useTheme();

  const dispatch = useDispatch();

  const profileSchema = useSelector(
    (state: AppState) => state.user?.profile?.schema
  ) as ProfileSchema;

  const [loading, setLoading] = useState(false);

  const handleFormSubmit = async (values: {
    currentLocationCoursePage: number;
    expYearCoursePage: number;
    phoneNumber: string;
    countryCode: string;
    currentOrganizationCoursePage?: string;
    name?: string;
  }) => {
    setLoading(true);
    const organization = values.currentOrganizationCoursePage;

    const infoString = getInfoString(profileSchema);
    const requiredString = getRequiredString({
      infoString,
      profileSchema,
      makeCurrentOrganizationMandatory: formik.values.expYearCoursePage > 1,
    });

    const name = values?.name?.trim();
    const phone = parseInt(values.phoneNumber, 10);
    const experienceYear = values.expYearCoursePage - 1;
    const currentLocation = values.currentLocationCoursePage;

    const updationResponse = await profileService.updateProfile(
      {
        pd: {
          infoString,
          requiredString,
          id: userId,
          phone,
          expYear: experienceYear,
          currentLocation: currentLocation.toString(),
          countryCode: values.countryCode,
          name,
          currentOrganization:
            (isAppHirist ? HIRISTORGANISATION : ORGANISATION).filter(
              org => org.value == organization
            )?.[0]?.label ?? organization,
        },
      },
      LEARN_GROW_COURSE_ENQUIRY_PROFILE_COMPLETION
    );

    if (updationResponse?.error) {
      if (updationResponse?.error?.code !== 0) {
        try {
          showSnackbar(updationResponse?.error?.message || '', {
            variant: 'error',
          });
        } catch {}
      }
      setLoading(false);
      return;
    }

    dispatch({
      type: 'User/set_user_profile',
      payload: {
        phone,
        experienceYear,
        locations: {
          current: {
            id: currentLocation,
          },
        },
        name,
      },
    });

    handleProfileUpdate();
  };

  const formik = useFormik({
    initialValues: {
      currentLocationCoursePage: 0,
      expYearCoursePage: 0,
      phoneNumber: '',
      countryCode: '+91',
      name: '',
      currentOrganizationCoursePage: '',
    },
    enableReinitialize: true,
    validationSchema: validationSchemaPersonalDetails,
    onSubmit: async values => {
      handleFormSubmit(values);
    },
  });

  const [menuOpen, setOpen] = useState<string | boolean>(false);
  const [mobileNumberFocused, setMobileNumberFocused] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setMenuOpen = (value: any) => {
    if (value && value === menuOpen) {
      setMenuOpen(false);
    } else setOpen(value);
  };

  useEffect(() => {
    const el = document.querySelector('.Mui-error, [data-error]');
    (el?.parentElement ?? el)?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, [formik?.isSubmitting]);

  const title = 'Fill in your details to express interest in this course';

  const isDeviceLarge = useIsLargeDevice('md');

  return (
    <>
      <Dialog
        variant={VersionVariants.VERSION_THREE}
        open
        title={title}
        onClose={() => closeEnquiryPopup()}
        sx={{
          '.MuiDialogContent-root': {
            p: 0,
            maxWidth: 477,
          },
          '.MuiIconButton-root': {
            mt: 2,
            ':hover': {
              bgcolor: 'transparent',
            },
          },
          mx: 0,
          '.MuiPaper-elevation24': {
            maxHeight: '100% !important',
          },
          ...(!isDeviceLarge && {
            '.MuiPaper-elevation': {
              maxHeight: 650,
            },
          }),
          ...(!isDeviceLarge && {
            '.MuiPaper-elevation24': {
              position: 'absolute',
              bottom: 0,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            },
          }),
        }}
        titleProps={{
          mt: 4,
          ml: 3,
          p: 0,
        }}
      >
        <FormControl
          id="personaldetailform"
          key={'personaldetailform'}
          sx={{
            pb: 3,
            px: 3,
            width: '100%',
          }}
        >
          <Name formik={formik} theme={theme} />

          <ExperienceYear
            formik={formik}
            menuOpen={menuOpen}
            setMenuOpen={setMenuOpen}
          />

          {formik.values.expYearCoursePage > 1 && (
            <CurrentOrganization
              formik={formik}
              theme={theme}
              menuOpen={menuOpen}
              setMenuOpen={setMenuOpen}
            />
          )}
          <CurrentLocation
            formik={formik}
            theme={theme}
            menuOpen={menuOpen}
            setMenuOpen={setMenuOpen}
          />

          <Phone
            formik={formik}
            menuOpen={menuOpen}
            setMenuOpen={setMenuOpen}
            mobileNumberFocused={mobileNumberFocused}
            setMobileNumberFocused={setMobileNumberFocused}
            countriesJson={countriesJson}
          />

          <ButtonV2
            type="submit"
            sx={{
              maxWidth: 150,
              height: 40,
              px: {xxs: 2, md: 6},
              py: 1,
              mt:
                formik.touched.phoneNumber && formik.errors.phoneNumber?.length
                  ? 2
                  : 4,
              backgroundColor: theme.palette.secondary.main,
              fontSize: theme.typography.pxToRem(18),
              fontWeight: 500,
              lineHeight: '24px',
              ...(process.env.NEXT_PUBLIC_APP_NAME === 'hirist' && {
                bgcolor: '#E9630C !important',
                borderRadius: '4px',
              }),
            }}
            variant="contained"
            size="large"
            onClick={() => formik.handleSubmit()}
            disabled={loading}
          >
            {loading ? (
              <Box
                sx={{
                  display: 'flex',
                  textAlign: 'center',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Loader
                  size={20}
                  sx={{
                    color: '#fff',
                  }}
                />
              </Box>
            ) : (
              'Submit'
            )}
          </ButtonV2>
        </FormControl>
      </Dialog>
    </>
  );
};

export default ProfileInfoPopup;
